// ─────────────────────────────────────────────────────────────────────────────
// Dashboard.js
// Main contractor dashboard — shows quote stats, recent quotes, and
// any active notifications pushed from the admin panel.
//
// SUSPENSION CHECK: On load, fetches the company status. If suspended,
// renders a fullscreen SuspendedScreen overlay that cannot be dismissed.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import Sidebar from '../components/Sidebar'
import SuspendedScreen from '../components/SuspendedScreen'
import { useNavigate } from 'react-router-dom'
import TrialBanner from '../components/TrialBanner'

export default function Dashboard({ session }) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [quotes, setQuotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('All')
  const [sortBy, setSortBy] = useState('newest')
  const navigate = useNavigate()

  // Notifications pushed from admin panel
  const [appNotifications, setAppNotifications] = useState([])
  const [dismissedNotifs, setDismissedNotifs] = useState([])

  // Suspension — if company is suspended, suspendedInfo will hold { reason }
  const [suspendedInfo, setSuspendedInfo] = useState(null)

  // ── Data fetching ──────────────────────────────────────────────────────────
  useEffect(() => {
    fetchQuotes()
    fetchNotifications()
  }, [])

  // Fetch active notifications from the notifications table (admin-managed)
  const fetchNotifications = async () => {
    const now = new Date().toISOString()
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('is_active', true)
      .or(`expires_at.is.null,expires_at.gt.${now}`) // hide expired notifications
      .order('created_at', { ascending: false })
    setAppNotifications(data || [])
  }

  // Fetch quotes for this contractor's company
  // Also checks company suspension status
  const fetchQuotes = async () => {
    // Step 1: Get user's profile to find their company_id
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', session.user.id)
      .single()

    if (!profile?.company_id) {
      setLoading(false)
      return
    }

    // Step 2: Check company suspension status
    const { data: company } = await supabase
      .from('companies')
      .select('status, suspend_reason')
      .eq('id', profile.company_id)
      .single()

    if (company?.status === 'suspended') {
      // Company is suspended — show the suspended screen
      setSuspendedInfo({ reason: company.suspend_reason })
      setLoading(false)
      return
    }

    // Step 3: Fetch quotes for the company
    const { data, error } = await supabase
      .from('quotes')
      .select(`*, customers(customer_name, customer_type), companies(company_name)`)
      .eq('company_id', profile.company_id)
      .order('created_at', { ascending: false })

    if (!error) setQuotes(data || [])
    setLoading(false)
  }

  // ── Filtering & sorting ────────────────────────────────────────────────────
  const filteredQuotes = quotes
    .filter(q => {
      const matchesStatus = statusFilter === 'All' || q.status === statusFilter
      const matchesSearch = search === '' ||
        q.customers?.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        `Q-${String(q.quote_number_numeric).padStart(3, '0')}`.toLowerCase().includes(search.toLowerCase())
      return matchesStatus && matchesSearch
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at)
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at)
      if (sortBy === 'highest') return parseFloat(b.total || 0) - parseFloat(a.total || 0)
      if (sortBy === 'lowest') return parseFloat(a.total || 0) - parseFloat(b.total || 0)
      return 0
    })

  // ── Stats cards ────────────────────────────────────────────────────────────
  const stats = [
    { label: 'Total Quotes', value: quotes.length, color: '#2563EB' },
    { label: 'Draft', value: quotes.filter(q => q.status === 'Draft').length, color: '#64748B' },
    { label: 'Sent', value: quotes.filter(q => q.status === 'Sent').length, color: '#2563EB' },
    { label: 'Accepted', value: quotes.filter(q => q.status === 'Accepted').length, color: '#15803D' },
  ]

  // ── Helpers ────────────────────────────────────────────────────────────────
  const statusStyle = (status) => {
    const map = {
      Draft:    { background: '#F1F5F9', color: '#64748B' },
      Sent:     { background: '#EFF6FF', color: '#2563EB' },
      Accepted: { background: '#F0FDF4', color: '#15803D' },
      Rejected: { background: '#FFF1F2', color: '#BE123C' },
    }
    return {
      ...(map[status] || map.Draft),
      padding: '3px 10px', borderRadius: '999px',
      fontSize: '12px', fontWeight: '600',
      display: 'inline-block', whiteSpace: 'nowrap',
    }
  }

  const selectStyle = {
    height: '40px', borderRadius: '10px', padding: '0 14px',
    border: '1.5px solid #E2E8F0', fontSize: '13px',
    outline: 'none', background: 'white', cursor: 'pointer',
    fontFamily: 'Inter, sans-serif', color: '#374151',
  }

  // ── Suspension check ───────────────────────────────────────────────────────
  // If the company is suspended, show the SuspendedScreen overlay.
  // This cannot be dismissed — the contractor must contact support.
  if (suspendedInfo) {
    return <SuspendedScreen reason={suspendedInfo.reason} />
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex', minHeight: '100vh',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      background: '#F7F8FC',
    }}>
      {/* ── Sidebar ── */}
      <Sidebar session={session} />
      

      {/* ── Admin Notification Bars ────────────────────────────────────────────
          Shown at the top of the page, above all content.
          Contractors can dismiss each one individually.
          Notifications are fetched from the notifications table (admin-managed).
          Only active, non-expired notifications are shown.
      ─────────────────────────────────────────────────────────────────────── */}
      {appNotifications
        .filter(n => !dismissedNotifs.includes(n.id))
        .map(notif => {
          const styles = {
            info:     { background: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
            warning:  { background: '#FFFBEB', color: '#B45309', border: '#FDE68A' },
            critical: { background: '#FFF1F2', color: '#BE123C', border: '#FECDD3' },
          }
          const s = styles[notif.type] || styles.info
          return (
            <div key={notif.id} style={{
              position: 'fixed', top: 0, left: '240px', right: 0, zIndex: 50,
              background: s.background, borderBottom: `1px solid ${s.border}`,
              padding: '12px 40px', display: 'flex',
              justifyContent: 'space-between', alignItems: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}>
              <p style={{ fontSize: '13px', fontWeight: '500', color: s.color, margin: 0 }}>
                {notif.message}
              </p>
              <button
                onClick={() => setDismissedNotifs(prev => [...prev, notif.id])}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '16px', color: s.color, fontWeight: '600',
                  padding: '0 0 0 16px',
                }}>×</button>
            </div>
          )
        })
      }

      {/* ── Main Content ── */}
      <div style={{ 
        marginLeft: '240px', flex: 1, padding: '36px 40px',
        paddingTop: appNotifications.filter(n => !dismissedNotifs.includes(n.id)).length > 0 ? '56px' : '36px',
      }}>

        {/* ── Page Header ── */}
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{
            fontSize: '22px', fontWeight: '800', color: '#0F172A',
            margin: '0 0 4px', letterSpacing: '-0.4px',
          }}>Dashboard</h1>
          <p style={{ fontSize: '13px', color: '#94A3B8', margin: 0 }}>
            Welcome back, {session?.user?.email}
          </p>
        </div>

        <TrialBanner session={session} />

        {/* ── Stats Cards ── */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '16px', marginBottom: '28px',
        }}>
          {stats.map((stat, i) => (
            <div key={i} style={{
              background: 'white', borderRadius: '12px',
              border: '1px solid #EAECF0', padding: '22px 24px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}>
              <p style={{
                fontSize: '11px', fontWeight: '600', color: '#94A3B8',
                textTransform: 'uppercase', letterSpacing: '0.6px', margin: '0 0 10px',
              }}>{stat.label}</p>
              <p style={{
                fontSize: '32px', fontWeight: '800', color: '#0F172A',
                margin: '0 0 4px', letterSpacing: '-1px',
              }}>{stat.value}</p>
              <div style={{
                height: '3px', borderRadius: '999px',
                background: '#F1F5F9', marginTop: '12px',
              }}>
                <div style={{
                  height: '100%', borderRadius: '999px',
                  background: stat.color,
                  width: quotes.length > 0 ? `${(stat.value / quotes.length) * 100}%` : '0%',
                  transition: 'width 0.6s ease',
                }} />
              </div>
            </div>
          ))}
        </div>

        {/* ── Recent Quotes Table ── */}
        <div style={{
          background: 'white', borderRadius: '12px',
          border: '1px solid #EAECF0', overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}>
          {/* Table title row */}
          <div style={{
            padding: '16px 24px', borderBottom: '1px solid #EAECF0',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <h2 style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', margin: 0 }}>
              Recent Quotes
            </h2>
            <span style={{ fontSize: '12px', color: '#94A3B8' }}>
              {filteredQuotes.length} of {quotes.length} quotes
            </span>
          </div>

          {/* Filters row */}
          <div style={{
            padding: '12px 24px', borderBottom: '1px solid #EAECF0',
            display: 'flex', gap: '12px', alignItems: 'center',
            background: '#FAFBFC',
          }}>
            <input
              type="text"
              placeholder="Search by customer or quote number..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                flex: 1, height: '36px', borderRadius: '8px',
                border: '1.5px solid #E2E8F0', padding: '0 12px',
                fontSize: '13px', outline: 'none', background: 'white',
                fontFamily: 'Inter, sans-serif',
              }}
            />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              style={{ ...selectStyle, height: '36px', minWidth: '130px' }}>
              <option value="All">All Statuses</option>
              <option value="Draft">Draft</option>
              <option value="Sent">Sent</option>
              <option value="Accepted">Accepted</option>
              <option value="Rejected">Rejected</option>
            </select>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              style={{ ...selectStyle, height: '36px', minWidth: '150px' }}>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="highest">Highest Value</option>
              <option value="lowest">Lowest Value</option>
            </select>
          </div>

          {/* Column headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '100px 1fr 100px 100px 110px 120px',
            padding: '10px 24px', background: '#F8FAFC',
            borderBottom: '1px solid #EAECF0',
          }}>
            {['Quote #', 'Customer', 'Total', 'Status', 'Date', 'Actions'].map((h, i) => (
              <span key={i} style={{
                fontSize: '11px', fontWeight: '600', color: '#94A3B8',
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>{h}</span>
            ))}
          </div>

          {/* Quote rows */}
          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#94A3B8', fontSize: '14px' }}>
              Loading...
            </div>
          ) : filteredQuotes.length === 0 ? (
            <div style={{ padding: '48px 24px', textAlign: 'center' }}>
              <p style={{ fontSize: '15px', fontWeight: '700', color: '#0F172A', margin: '0 0 6px' }}>
                {quotes.length === 0 ? 'No quotes yet' : 'No quotes match your filters'}
              </p>
              <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 20px' }}>
                {quotes.length === 0
                  ? 'Go to Quotes to create your first one'
                  : 'Try changing your search or filters'}
              </p>
              {quotes.length === 0 && (
                <button onClick={() => navigate('/quotes')} style={{
                  padding: '8px 20px', borderRadius: '8px',
                  background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                  color: 'white', fontSize: '13px', fontWeight: '600',
                  border: 'none', cursor: 'pointer',
                }}>Go to Quotes</button>
              )}
            </div>
          ) : (
            filteredQuotes.map((quote, i) => (
              <div key={quote.id} style={{
                display: 'grid',
                gridTemplateColumns: '100px 1fr 100px 100px 110px 120px',
                padding: '14px 24px', alignItems: 'center',
                borderBottom: i < filteredQuotes.length - 1 ? '1px solid #F1F5F9' : 'none',
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#FAFBFC'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#2563EB' }}>
                  Q-{String(quote.quote_number_numeric).padStart(3, '0')}
                </span>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A', margin: '0 0 2px' }}>
                    {quote.customers?.customer_name || '—'}
                  </p>
                  <span style={{ fontSize: '11px', color: '#94A3B8' }}>
                    {quote.customers?.customer_type || ''}
                  </span>
                </div>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A' }}>
                  £{parseFloat(quote.total || 0).toFixed(2)}
                </span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={statusStyle(quote.status)}>{quote.status}</span>
                </div>
                <span style={{ fontSize: '12px', color: '#94A3B8' }}>
                  {new Date(quote.created_at).toLocaleDateString('en-GB')}
                </span>
                <button
                  onClick={() => navigate(`/quotes/${quote.id}`)}
                  style={{
                    padding: '5px 12px', borderRadius: '6px', fontSize: '12px',
                    fontWeight: '600', border: '1.5px solid #E2E8F0',
                    background: 'white', color: '#374151', cursor: 'pointer',
                    width: 'fit-content',
                  }}
                >View</button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
